import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import useModuleStats from './useModuleStats';
import { getChartColor } from '../../../config/statusColors';
import { formatShortDate } from '../overview/format';
import '../dashboard.css';

interface RecentItemsProps {
  /** List endpoint, e.g. "/transactions/". page_size/ordering are appended. */
  endpoint: string;
  /** Section-local heading, e.g. "Recent Applications". */
  title: string;
  /** Row link target, e.g. (item) => `/transactions/${item.id}`. */
  itemLink: (item: any) => string;
  /** Main row text. */
  primary: (item: any) => React.ReactNode;
  /** Secondary muted text under the title (id, client, …). */
  secondary?: (item: any) => React.ReactNode;
  /** Status key for the badge (localized via `labels`). */
  statusKey?: (item: any) => string | undefined;
  /** status key -> localized label (from statusLabels.ts). */
  labels?: Record<string, string>;
  limit?: number;
}

/**
 * Pill colors derived from the canonical status palette (statusColors.ts):
 * a light tint for the background, a darkened shade for text, the pure
 * color for the dot — so every module's statuses stay in one family.
 */
const pillStyle = (status: string): React.CSSProperties => {
  const c = getChartColor(status);
  return {
    background: `color-mix(in srgb, ${c} 12%, white)`,
    color: `color-mix(in srgb, ${c} 78%, black)`,
    ['--dash-pill-dot' as any]: c,
  };
};

/**
 * Compact "recent items" list rendered below a section's charts.
 * Fetches the module's own list endpoint so role/tenant scoping is inherited;
 * errors (403 etc.) collapse the list silently, same as useModuleStats.
 */
const RecentItems: React.FC<RecentItemsProps> = ({
  endpoint,
  title,
  itemLink,
  primary,
  secondary,
  statusKey,
  labels = {},
  limit = 5,
}) => {
  const { isRTL } = useLanguage();
  const sep = endpoint.includes('?') ? '&' : '?';
  const { data, loading, error } = useModuleStats<any>(
    `${endpoint}${sep}page_size=${limit}&ordering=-created_at`
  );

  if (error) return null;
  const items: any[] = Array.isArray(data) ? data : data?.results || [];
  if (!loading && items.length === 0) return null;

  return (
    <div>
      <div className="dash-subtitle">{title}</div>
      {loading ? (
        <div className="flex flex-col gap-2" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i} className="dash-skel" style={{ height: 36 }} />
          ))}
        </div>
      ) : (
        <ul className="dash-recent">
          {items.slice(0, limit).map((item, idx) => {
            const status = statusKey?.(item);
            return (
              <li key={item.id ?? idx}>
                <div className="min-w-0">
                  <Link to={itemLink(item)} className="block truncate">
                    {primary(item)}
                  </Link>
                  {secondary && (
                    <div className="dash-project-code truncate">{secondary(item)}</div>
                  )}
                </div>
                <div className="dash-recent-meta">
                  {status && (
                    <span className="dash-pill" style={pillStyle(status)}>
                      {labels[status] || status}
                    </span>
                  )}
                  <span className="dash-date">{formatShortDate(item.created_at, isRTL)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RecentItems;
